import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { Card } from "@/components/ui/card";
import { CalendarClock, CheckCircle2, AlertCircle, ChevronRight } from "lucide-react";

const fmtPct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

const formatShortDate = (iso, locale) => {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(locale === "en" ? "en-US" : "es-CR", {
      day: "2-digit",
      month: "short",
    });
  } catch {
    return iso;
  }
};

export default function ScheduleCoverage() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api
      .get("/stats/schedule-coverage")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data) return null;

  const groups = data.groups || [];
  const overallPct = fmtPct(data.total_reported, data.total_expected);

  return (
    <div className="mt-8" data-testid="schedule-coverage-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-brand-blue" />
            <h2 className="font-display text-xl font-bold text-slate-900">{t("schedule_coverage")}</h2>
          </div>
          <p className="text-xs text-slate-500 mt-1">{t("schedule_coverage_desc")}</p>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider text-slate-500">{data.month}</div>
          <div className="font-display text-2xl font-bold text-slate-900">
            {data.total_reported} <span className="text-slate-400">/</span> {data.total_expected}
          </div>
          <div
            className={`text-[11px] font-medium ${
              overallPct >= 90 ? "text-green-600" : overallPct >= 60 ? "text-orange-600" : "text-red-600"
            }`}
          >
            {overallPct}%
          </div>
        </div>
      </div>

      {groups.length === 0 ? (
        <Card className="p-6 text-center text-sm text-slate-500" data-testid="no-coverage-data">
          {t("no_schedule_coverage")}
        </Card>
      ) : (
        <div className="space-y-2">
          {groups.map((g) => {
            const pct = fmtPct(g.reported_count, g.expected_count);
            const isComplete = g.missing_count === 0 && g.expected_count > 0;
            const isOpen = !!expanded[g.group_id];
            const barColor =
              pct >= 90 ? "bg-green-500" : pct >= 60 ? "bg-orange-500" : "bg-red-500";
            return (
              <Card
                key={g.group_id}
                className="p-4 hover:border-slate-300 transition-colors"
                data-testid={`coverage-row-${g.group_id}`}
              >
                <button
                  type="button"
                  onClick={() =>
                    setExpanded({ ...expanded, [g.group_id]: !isOpen })
                  }
                  className="w-full flex items-center gap-4 text-left"
                  disabled={g.missing_count === 0}
                >
                  <div className="flex-shrink-0">
                    {isComplete ? (
                      <CheckCircle2 className="h-6 w-6 text-green-500" />
                    ) : (
                      <AlertCircle
                        className={`h-6 w-6 ${
                          pct >= 60 ? "text-orange-500" : "text-red-500"
                        }`}
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900">{g.group_name}</span>
                      {g.is_private && (
                        <span className="text-[10px] uppercase tracking-wider text-brand bg-brand/10 px-1.5 py-0.5 rounded">
                          {t("private")}
                        </span>
                      )}
                      {g.schedule_label && (
                        <span className="text-xs text-slate-500">· {g.schedule_label}</span>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${barColor} transition-all`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="text-xs text-slate-600 whitespace-nowrap">
                        {g.reported_count} / {g.expected_count}
                        <span className="text-slate-400 ml-1">({pct}%)</span>
                      </div>
                    </div>
                  </div>
                  {g.missing_count > 0 && (
                    <ChevronRight
                      className={`h-4 w-4 text-slate-400 transition-transform ${
                        isOpen ? "rotate-90" : ""
                      }`}
                    />
                  )}
                </button>

                {isOpen && g.missing_dates?.length > 0 && (
                  <div
                    className="mt-3 pt-3 border-t border-slate-100"
                    data-testid={`coverage-missing-${g.group_id}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                      {t("missing_dates")} ({g.missing_count})
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {g.missing_dates.map((d) => (
                        <span
                          key={d}
                          className="text-xs px-2 py-1 rounded bg-red-50 text-red-700 border border-red-100"
                        >
                          {formatShortDate(d, lang)}
                        </span>
                      ))}
                      {g.missing_count > g.missing_dates.length && (
                        <span className="text-xs px-2 py-1 text-slate-400">
                          +{g.missing_count - g.missing_dates.length}…
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
